import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, MessageCircle, Calendar, Clock, ArrowRight, Receipt, Ticket } from 'lucide-react';
import { ReserveSeatModal } from '../components/ReserveSeatModal';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_build-on-prompt/artifacts/4nyosk34_Logo%20-%20GrowUp1Again.png";

const prettyDate = (value) => {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  const d = new Date(`${value}T00:00:00`);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const KEYS = {
  registration: 'thank-you-registration',
  payment: 'thank-you-payment',
};

const resolveKey = (kind) => KEYS[kind] || (kind ? `thank-you-${kind}` : '');

// The visitor arrives straight from the landing page, so its cached payload is warm:
// use it to show the WhatsApp button and session details instantly.
function cachedSession(slug) {
  try {
    const raw = localStorage.getItem(`lp:${slug || ''}`) || localStorage.getItem(
      Object.keys(localStorage).find(k => k.startsWith('lp:')) || ''
    );
    if (!raw) return null;
    const { payload } = JSON.parse(raw);
    return { ...payload.webinar, whatsapp_url: payload.page.content.whatsapp_url };
  } catch { return null; }
}

function cachedContent(pageKey) {
  try { return JSON.parse(localStorage.getItem(`ty:${pageKey}`)) || null; } catch { return null; }
}

function cachedVariant(pageKey, kind) {
  return localStorage.getItem(`tyv:${pageKey}`) || (kind === 'payment' ? 'payment' : 'registration');
}

export default function ThankYou() {
  const { kind } = useParams();
  const [params] = useSearchParams();
  const { token } = useAuth();
  const pageKey = resolveKey(kind);
  const slug = params.get('slug');
  const orderId = params.get('order');
  const [content, setContent] = useState(() => cachedContent(pageKey));
  const [variant, setVariant] = useState(() => cachedVariant(pageKey, kind));
  const [session, setSession] = useState(() => (kind === 'registration' ? cachedSession(slug) : null));
  const [receipt, setReceipt] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserved, setReserved] = useState(false);

  useEffect(() => {
    if (!pageKey) { setNotFound(true); return; }
    axios.get(`${API}/site-pages/${pageKey}`)
      .then(r => {
        setContent(r.data.content);
        setVariant(r.data.variant || 'registration');
        try {
          localStorage.setItem(`ty:${pageKey}`, JSON.stringify(r.data.content));
          localStorage.setItem(`tyv:${pageKey}`, r.data.variant || 'registration');
        } catch { }
      })
      .catch(() => { if (!cachedContent(pageKey)) setNotFound(true); });
  }, [pageKey]);

  useEffect(() => {
    if (variant === 'payment') return;
    const url = slug ? `${API}/landing/${slug}` : `${API}/landing-current`;
    axios.get(url)
      .then(r => setSession({ ...r.data.webinar, whatsapp_url: r.data.page.content.whatsapp_url }))
      .catch(() => { });
  }, [slug, variant]);
  useEffect(() => {
    if (!orderId || !token) return;
    axios.get(`${API}/payments/receipt/${orderId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setReceipt(r.data))
      .catch(() => { });
  }, [orderId, token]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="thank-you-not-found">
        <div className="text-center">
          <p className="font-['Barlow_Condensed'] text-3xl font-bold uppercase mb-3">Page not found</p>
          <Link to="/" className="text-[#00D4FF] text-sm hover:underline">Back to home</Link>
        </div>
      </div>
    );
  }

  if (!content) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#FF1493] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const isPayment = variant === 'payment';
  const whatsappUrl = content.whatsapp_url || session?.whatsapp_url || '';
  const showReserve = !!content.reserve_enabled;

  return (
    <div className="min-h-screen" data-testid={`thank-you-${kind}`}>
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center animate-fade-up">
          <img src={LOGO_URL} alt="Grow Up Once Again" className="h-20 w-auto object-contain mix-blend-screen mx-auto mb-10" />

          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00FF9D]/10 border border-[#00FF9D]/30 mb-6">
            <CheckCircle2 size={30} className="text-[#00FF9D]" />
          </div>

          <p className="text-[11px] uppercase tracking-[0.3em] text-[#00FF9D] mb-4">{content.eyebrow}</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] mb-4">
            {content.title}<br />
            <span className="bg-gradient-to-r from-[#FF1493] via-[#8B00FF] to-[#00D4FF] bg-clip-text text-transparent">{content.title_accent}</span>
          </h1>
          <p className="text-base text-white/55 max-w-xl mx-auto leading-relaxed">{content.subtitle}</p>
        </div>

        {/* WhatsApp + optional Reserve My Seat (registration only) */}
        {!isPayment && (whatsappUrl || showReserve) && (
          <div className="mt-12 text-center animate-fade-up">
            <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-4 ${whatsappUrl && showReserve ? "sm:justify-evenly sm:gap-0" : "sm:justify-center"}`} data-testid="thank-you-cta-row">
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-cta"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl w-full sm:w-auto bg-[#25D366] text-[#052e16] font-bold text-sm uppercase tracking-wide hover:brightness-110 transition-all"
                >
                  <MessageCircle size={18} /> {content.whatsapp_cta}
                </a>
              )}
              {showReserve && (
                <button
                  onClick={() => setReserveOpen(true)}
                  data-testid="reserve-seat-cta"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl w-full sm:w-auto btn-neon text-sm uppercase tracking-wide"
                >
                  {reserved ? <CheckCircle2 size={18} /> : <Ticket size={18} />} {reserved ? 'Seat Reserved' : content.reserve_button_label}
                </button>
              )}
            </div>
            {whatsappUrl && <p className="text-xs text-white/40 mt-4 max-w-md mx-auto">{content.whatsapp_note}</p>}
          </div>
        )}

        {reserveOpen && (
          <ReserveSeatModal pageKey={pageKey} slug={slug} content={content}
            onClose={() => setReserveOpen(false)} onDone={() => setReserved(true)} />
        )}

        {/* Session details */}
        {!isPayment && session?.title && (
          <div className="mt-12 glass rounded-2xl p-6" data-testid="session-details">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 mb-4">{content.details_title}</p>
            <p className="font-['Barlow_Condensed'] text-2xl font-bold uppercase mb-4">{session.title}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
              <span className="flex items-center gap-2 text-white/70"><Calendar size={14} className="text-[#FF1493]" /> {prettyDate(session.session_date) || 'To be announced'}</span>
              <span className="flex items-center gap-2 text-white/70"><Clock size={14} className="text-[#00D4FF]" /> {session.session_time || 'To be announced'}</span>
            </div>
          </div>
        )}

        {/* Order summary */}
        {isPayment && receipt && (
          <div className="mt-12 glass rounded-2xl p-6" data-testid="order-summary">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 mb-4 flex items-center gap-2"><Receipt size={12} /> {content.details_title}</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-4"><span className="text-white/40">Program</span><span className="font-semibold text-right">{receipt.course_title}</span></div>
              <div className="flex justify-between gap-4"><span className="text-white/40">Amount Paid</span><span className="font-semibold text-[#00FF9D]">&#8377;{receipt.amount?.toLocaleString()}</span></div>
              <div className="flex justify-between gap-4"><span className="text-white/40">Payment ID</span><span className="font-mono text-[11px] text-white/60 text-right break-all">{receipt.payment_id || receipt.order_id}</span></div>
              <div className="flex justify-between gap-4"><span className="text-white/40">Date</span><span className="text-white/60">{(receipt.paid_at || '').slice(0, 10)}</span></div>
            </div>
          </div>
        )}

        {/* What happens next */}
        {content.next_points?.length > 0 && (
          <div className="mt-10">
            <p className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-5">{content.next_title}</p>
            <div className="space-y-3">
              {content.next_points.map((p, i) => (
                <div key={i} className="flex gap-3 glass rounded-xl p-4" data-testid={`next-point-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#FF1493] to-[#8B00FF] flex items-center justify-center text-[11px] font-bold shrink-0">{i + 1}</span>
                  <p className="text-sm text-white/65 leading-relaxed">{p}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTAs — the registration page keeps only the WhatsApp action above */}
        {isPayment && (
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            {content.primary_cta_label && (
              <Link to={content.primary_cta_url || '/dashboard'} className="btn-neon px-8 py-3.5 rounded-xl text-sm flex items-center gap-2" data-testid="primary-cta">
                {content.primary_cta_label} <ArrowRight size={15} />
              </Link>
            )}
            {content.secondary_cta_label && (
              <Link to={content.secondary_cta_url || '/courses'} className="btn-neon-outline px-8 py-3.5 rounded-xl text-sm" data-testid="secondary-cta">
                {content.secondary_cta_label}
              </Link>
            )}
          </div>
        )}

        {isPayment && content.support_note && (
          <p className="text-xs text-white/35 text-center mt-8">{content.support_note}</p>
        )}
        <p className="text-sm text-white/60 text-center mt-12" data-testid="page-footer-note">{content.footer_note}</p>
      </div>
    </div>
  );
}
